import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useSiteSettings } from "@/lib/settings";
import { computeHealthScore, scoreBand } from "@/lib/health";
import {
  Upload, FileText, Calendar, Sparkles, ArrowRight, TrendingUp, TrendingDown,
  MessageCircle, Users, Lightbulb, ShieldAlert, ChevronRight, Building2, Gift,
} from "lucide-react";

export default function PatientDashboard() {
  const { user } = useAuth();
  const { platform } = useSiteSettings();
  const marketplaceOn = platform?.marketplace_enabled !== false;
  const [reports, setReports] = useState([]);
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data));
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  const latest = reports.find((r) => r.status === "ready" || r.status === "published");
  const healthScore = useMemo(() => (latest ? computeHealthScore(latest.smart_report) : null), [latest]);
  const band = scoreBand(healthScore);
  const totalNeed = reports.reduce((s, r) => s + Number(r.smart_report?.parameters_needing_attention || 0), 0);
  const labIds = new Set(reports.filter((r) => r.lab_id).map((r) => r.lab_id));

  const insights = useMemo(() => buildInsights(reports, marketplaceOn), [reports, marketplaceOn]);
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <DashboardLayout role="patient">
      <div className="max-w-6xl">
        {/* Greeting */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Welcome back{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}</div>
            <div className="text-sm text-ink/50">{dateStr}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {marketplaceOn && (
              <Link to="/book-tests"><Button data-testid="hdr-book" className="bg-efgh-orange hover:bg-efgh-orange/90 text-white shadow-sm"><Calendar className="w-4 h-4 mr-2" />Book a test</Button></Link>
            )}
            <Link to="/patient/upload"><Button data-testid="hdr-upload" variant="outline" className="border-ink text-ink hover:bg-muted"><Upload className="w-4 h-4 mr-2" />Upload a report</Button></Link>
          </div>
        </div>

        <h1 className="mt-2 font-heading font-bold text-3xl sm:text-4xl text-ink">Hello{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""} 👋</h1>
        {latest?.smart_report?.ai_summary ? (
          <p className="mt-2 text-ink/60 max-w-2xl">Your last report was on {latest.report_date || latest.created_at?.slice(0,10)}. {shortSummary(latest.smart_report.ai_summary)}</p>
        ) : (
          <p className="mt-2 text-ink/60 max-w-2xl">Upload your first lab report and get an AI-powered smart report in under a minute.</p>
        )}

        {/* Hero card: Health score */}
        {latest && (
          <Card className="mt-6 p-6 rounded-2xl overflow-hidden relative">
            <div className="grid md:grid-cols-[220px_1fr_auto] gap-6 items-center">
              <HealthScoreRing score={healthScore} band={band} />
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Latest Smart Report</div>
                <div className="mt-1 font-heading font-bold text-2xl text-ink">{latest.package_name || "Diagnostic Report"}</div>
                <div className="text-sm text-ink/60 mt-1">{latest.report_date || latest.created_at?.slice(0,10)}</div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <span className="pill pill-high">{latest.smart_report?.parameters_needing_attention || 0} attention</span>
                  <span className="pill pill-borderline">{latest.smart_report?.parameters_borderline || 0} watch</span>
                  <span className="pill pill-normal">{latest.smart_report?.parameters_normal || 0} normal</span>
                </div>
              </div>
              <Link to={`/report/${latest.id}`}><Button data-testid="view-latest" className="bg-ink hover:bg-ink/90 text-white h-11">View report<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
          </Card>
        )}

        {/* KPI strip */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={FileText} color="text-efgh-blue" bg="bg-efgh-blue/10" value={reports.length} label="Total reports" to="/patient/reports" testid="kpi-total-reports" />
          <KpiCard icon={ShieldAlert} color="text-danger" bg="bg-danger-light" value={totalNeed} label="Need attention" to="/patient/reports?attention=1" testid="kpi-need-attention" />
          <KpiCard icon={Building2} color="text-efgh-green" bg="bg-efgh-green/10" value={labIds.size} label="Labs you've used" to="/patient/reports" testid="kpi-labs-used" />
          <KpiCard icon={Calendar} color="text-efgh-orange" bg="bg-efgh-orange/10" value={bookings.length} label="Bookings" to="/patient/bookings" testid="kpi-bookings" />
        </div>

        {/* For you */}
        <div className="mt-8">
          <h2 className="font-heading font-semibold text-xl text-ink flex items-center gap-2"><Lightbulb className="w-5 h-5 text-efgh-yellow" /> For you</h2>
          <div className="mt-3 grid md:grid-cols-3 gap-3">
            {insights.map((it, i) => (
              <Card key={i} className={`p-5 rounded-2xl border-l-4`} style={{ borderLeftColor: it.color }}>
                <div className="text-2xl">{it.icon}</div>
                <div className="mt-3 text-sm text-ink/85" dangerouslySetInnerHTML={{ __html: it.body }} />
                {it.cta && <Link to={it.cta.to} className="mt-3 inline-flex items-center gap-1 text-sm font-medium" style={{ color: it.color }}>{it.cta.label} <ArrowRight className="w-3.5 h-3.5" /></Link>}
              </Card>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading font-semibold text-xl text-ink">Your health timeline</h2>
              <p className="text-sm text-ink/60">Every report you&apos;ve taken, from every lab, in one place.</p>
            </div>
            <Link to="/patient/reports" className="text-sm text-ink font-medium underline underline-offset-4">See all →</Link>
          </div>
          <Card className="rounded-2xl p-2">
            {reports.length === 0 && (
              <div className="p-10 text-center text-ink/50">
                <Sparkles className="w-6 h-6 mx-auto text-efgh-green" />
                <div className="mt-3">No reports yet. Upload your first one!</div>
              </div>
            )}
            <div className="divide-y divide-border">
              {reports.slice(0, 6).map((r) => <TimelineRow key={r.id} r={r} />)}
              <Link to="/patient/upload" data-testid="timeline-add" className="flex items-center gap-3 p-4 hover:bg-muted transition-all group">
                <div className="w-10 h-10 rounded-xl border-2 border-dashed border-ink/20 grid place-items-center text-ink/50 group-hover:border-ink group-hover:text-ink"><Upload className="w-5 h-5" /></div>
                <div className="text-sm text-ink/60 group-hover:text-ink">Add a report</div>
              </Link>
            </div>
          </Card>
        </div>

        {/* Quick actions */}
        <div className={`mt-8 grid gap-3 ${marketplaceOn ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          <ActionCard to="/patient/upload" testId="qa-upload" icon={Upload} title="Upload a new report" color="text-efgh-orange" bg="bg-efgh-orange/10" />
          <ActionCard to={latest ? `/report/${latest.id}` : "/patient/reports"} testId="qa-ask" icon={MessageCircle} title="Ask AI about your health" color="text-efgh-blue" bg="bg-efgh-blue/10" />
          {marketplaceOn && (
            <ActionCard to="/book-tests" testId="qa-book" icon={Calendar} title="Book a follow-up test" color="text-efgh-green" bg="bg-efgh-green/10" />
          )}
          <ActionCard to="/patient/referrals" testId="qa-refer" icon={Gift} title="Refer & Earn" color="text-efgh-yellow" bg="bg-efgh-yellow/10" />
        </div>
      </div>
    </DashboardLayout>
  );
}

function HealthScoreRing({ score, band }) {
  const pct = score ?? 0;
  const C = 2 * Math.PI * 42;
  const dash = (pct / 100) * C;
  return (
    <div className="relative w-[180px] h-[180px] mx-auto" data-testid="dashboard-health-score">
      <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
        <circle cx="50" cy="50" r="42" strokeWidth="7" stroke="#EEF0F4" fill="none" />
        <circle cx="50" cy="50" r="42" strokeWidth="7" stroke={band.color} fill="none" strokeLinecap="round" strokeDasharray={`${dash} ${C}`} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ink/50">Health Score</div>
          <div className="font-heading font-bold text-5xl text-ink leading-none">{score ?? "—"}</div>
          <div className="mt-1 text-xs font-medium" style={{ color: band.color }}>{band.label}</div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, color, bg, value, label, to, testid }) {
  const inner = (
    <Card className={`p-5 rounded-2xl ${to ? "card-hover cursor-pointer transition-transform hover:-translate-y-0.5" : ""}`} data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-heading font-bold text-3xl text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">{label}</div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function ActionCard({ to, icon: Icon, title, color, bg, testId }) {
  return (
    <Link to={to} data-testid={testId}>
      <Card className="p-5 rounded-2xl card-hover h-full">
        <div className={`w-11 h-11 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
        <div className="mt-4 font-heading font-semibold text-ink">{title}</div>
      </Card>
    </Link>
  );
}

function TimelineRow({ r }) {
  const need = Number(r.smart_report?.parameters_needing_attention || 0);
  const bord = Number(r.smart_report?.parameters_borderline || 0);
  const summary = need > 0 ? `${need} need attention` : bord > 0 ? `${bord} to watch` : "All normal";
  return (
    <Link to={`/report/${r.id}`} data-testid={`timeline-${r.id}`} className="flex items-center gap-4 p-4 hover:bg-muted transition-all">
      <div className="w-11 h-11 rounded-xl grid place-items-center text-ink font-heading font-bold shrink-0" style={{ background: `linear-gradient(135deg, ${labColor(r.lab_id, 0)}30, ${labColor(r.lab_id, 1)}30)` }}>
        {(r.package_name || r.patient_name || "R")[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-ink truncate">{r.package_name || "Report"}</div>
        <div className="text-xs text-ink/50">{r.report_date || r.created_at?.slice(0,10)} · {r.patient_name}</div>
      </div>
      <span className={`pill ${need > 0 ? "pill-high" : bord > 0 ? "pill-borderline" : "pill-normal"}`}>{summary}</span>
      <ChevronRight className="w-4 h-4 text-ink/30 shrink-0" />
    </Link>
  );
}

/* ---------- helpers ---------- */
function shortSummary(text) {
  if (!text) return "";
  const t = String(text).replace(/\s+/g, " ");
  return t.length > 180 ? t.slice(0, 180) + "…" : t;
}

function labColor(labId, idx) {
  const palette = [["#E55A25", "#F5C400"], ["#4CAF50", "#5A5AC7"], ["#F5C400", "#4CAF50"], ["#5A5AC7", "#E55A25"]];
  const hash = String(labId || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return palette[hash % palette.length][idx];
}

function buildInsights(reports, marketplaceOn = true) {
  const ready = reports.filter((r) => r.status === "ready" || r.status === "published");
  const insights = [];

  // Rising biomarker across reports
  if (ready.length >= 2) {
    const first = ready[ready.length - 1]?.smart_report?.biomarkers || [];
    const last = ready[0]?.smart_report?.biomarkers || [];
    for (const b of last) {
      const prev = first.find((x) => x.name === b.name);
      if (!prev) continue;
      const nv = parseFloat(b.value), pv = parseFloat(prev.value);
      if (isNaN(nv) || isNaN(pv)) continue;
      if (b.status === "high" && nv > pv * 1.05) {
        insights.push({
          icon: "⚠️",
          color: "#E55A25",
          body: `<b>Your ${b.name} has risen</b> from ${prev.value} to ${b.value} ${b.unit}. Consider a follow-up test.`,
          cta: marketplaceOn ? { to: "/book-tests", label: "Book follow-up test" } : null,
        });
        break;
      }
    }
  }

  // Untested vitamins
  const lastReport = ready[0];
  const snapshot = lastReport?.smart_report?.health_snapshot || [];
  const notTested = snapshot.filter((s) => s.status === "not_tested").map((s) => s.name);
  if (notTested.length > 0) {
    insights.push({
      icon: "💡",
      color: "#5A5AC7",
      body: `<b>You haven&apos;t tested ${notTested[0]} recently.</b> A retest is recommended every couple of months.`,
      cta: marketplaceOn ? { to: "/book-tests", label: "Book this test" } : null,
    });
  }

  // Good news
  const normals = lastReport?.smart_report?.parameters_normal || 0;
  if (normals > 15) {
    insights.push({
      icon: "✅",
      color: "#4CAF50",
      body: `<b>${normals} of your parameters look excellent</b> in your latest report. Keep up whatever you&apos;re doing!`,
    });
  }

  if (insights.length === 0) {
    insights.push({
      icon: "✨",
      color: "#5A5AC7",
      body: `Upload another lab report to unlock personalised insights across your history.`,
      cta: { to: "/patient/upload", label: "Upload a report" },
    });
  }
  return insights.slice(0, 3);
}
